/**
 * useChat — companion (parent) chat hook.
 *
 * Adapter over the shared useChatCore engine (#931): the core owns the base
 * transcript state machine (messages/status/typing/streaming/error, optimistic
 * send, history load, conversation switching); this hook layers on what is
 * genuinely companion-shaped — intent/routing/safety/escalation SSE events,
 * pending action cards with Undo, the handoff lifecycle (TRFer join/leave,
 * SLA breach), conversation mode, and the receipt attachment flow.
 */

import React, { useEffect, useRef, useCallback, useState, useReducer } from 'react';
import i18n from 'i18next';
import { chatApi } from '@/services/chatApi';
import { api } from '@/services/api';
import { useToast } from '@/components/ui/use-toast';
import { ToastAction, type ToastActionElement } from '@/components/ui/toast';
import { useChatCore, type ChatCoreHelpers } from './useChatCore';
import { useCompanionName } from './useCompanionName';
import type {
  ChatMessageState,
  ConversationMode,
  SSEIntentPayload,
  SSERoutingPayload,
  SSESafetyCheckPayload,
  SSEEscalationPayload,
  PendingAction,
  PendingReceipt,
} from '@/types/chat';
import type { ExpenseCategory } from '@/lib/bills-display';
import { systemRow, type SystemKind } from '@/features/chat-trf/adapter/messages';
import { isEnglishUi } from '@/features/chat-trf/language';

/**
 * #2109 R3 L5b: copy the hook builds, in the reader's language. The SSE
 * handlers are render-stable (read through a ref in the core), so they read
 * the live runtime rather than a `t` captured at mount.
 */
function tr(key: string, params?: Record<string, unknown>): string {
  return (i18n.t as unknown as (k: string, p?: Record<string, unknown>) => string)(key, params);
}

/** R3-B11: a server or engine sentence is English and reaches only an English reader. */
function serverTextOr(serverText: string, localizedKey: string): string {
  return isEnglishUi(i18n.language) ? serverText : tr(localizedKey);
}

/** A client-built handoff line: `{kind, params}` for display, the English line as `content` (R3-B15). */
function systemMessage(
  id: string,
  kind: SystemKind,
  params: Record<string, unknown>,
  metadata: ChatMessageState['metadata'],
): ChatMessageState {
  return systemRow({ id, timestamp: new Date().toISOString(), metadata }, kind, params);
}

// Named SSE events beyond the core set. Module-level const: useChatCore
// requires customEvents to be render-stable.
//
// Note: handoff events ('trfer_message', 'handback', 'reassigned',
// 'ai_cancelled') are NOT registered here — the chat backend pushes them
// as default-framed `data:` payloads, so they land on the default
// 'message' listener and useSSEConnection routes them to onCustomEvent
// by detecting `data.type`.
// 'ui_chips' and 'crisis_signal' (#1731 F-3): the core-vocabulary frames the
// stream route emits; the engine keeps chips, the page may act on the signal.
const CHAT_CUSTOM_EVENTS = ['intent', 'routing', 'safety_check', 'escalation', 'action', 'ui_chips', 'memory_recall', 'crisis_signal'];

// ─── Companion-specific state (beyond the shared core) ───────────

interface CompanionState {
  activeAgent: string | null;
  activeIntent: string | null;
  escalation: SSEEscalationPayload | null;
  safetyCheck: SSESafetyCheckPayload | null;
  pendingAction: PendingAction | null;
}

type CompanionAction =
  | { type: 'SET_ACTIVE_AGENT'; payload: string | null }
  | { type: 'SET_ACTIVE_INTENT'; payload: string | null }
  | { type: 'SET_ESCALATION'; payload: SSEEscalationPayload | null }
  | { type: 'SET_SAFETY_CHECK'; payload: SSESafetyCheckPayload | null }
  | { type: 'SET_PENDING_ACTION'; payload: PendingAction }
  | { type: 'CONFIRM_PENDING_ACTION'; payload: { confirmationMessage: string } }
  | { type: 'CLEAR_PENDING_ACTION' }
  | { type: 'RESET' };

const initialCompanionState: CompanionState = {
  activeAgent: null,
  activeIntent: null,
  escalation: null,
  safetyCheck: null,
  pendingAction: null,
};

function companionReducer(state: CompanionState, action: CompanionAction): CompanionState {
  switch (action.type) {
    case 'SET_ACTIVE_AGENT':
      return { ...state, activeAgent: action.payload };
    case 'SET_ACTIVE_INTENT':
      return { ...state, activeIntent: action.payload };
    case 'SET_ESCALATION':
      return { ...state, escalation: action.payload };
    case 'SET_SAFETY_CHECK':
      return { ...state, safetyCheck: action.payload };
    case 'SET_PENDING_ACTION':
      return { ...state, pendingAction: action.payload };
    case 'CONFIRM_PENDING_ACTION':
      if (!state.pendingAction) return state;
      return {
        ...state,
        pendingAction: {
          ...state.pendingAction,
          confirmed: true,
          confirmationMessage: action.payload.confirmationMessage,
        },
      };
    case 'CLEAR_PENDING_ACTION':
      return { ...state, pendingAction: null };
    case 'RESET':
      return initialCompanionState;
    default:
      return state;
  }
}

// ─── Hook ────────────────────────────────────────────────────────

export function useChat(initialConversationId: string) {
  const { toast } = useToast();
  const [companion, companionDispatch] = useReducer(companionReducer, initialCompanionState);

  // Conversation mode lives outside the reducers because it's also driven by
  // the conversations summary fetched in ChatPage, not just SSE events.
  const [mode, setMode] = useState<ConversationMode>('ai');

  // #639 — the family's own companion persona, for the system messages this
  // hook synthesizes. Held in a ref so the SSE bridge callbacks can read the
  // latest value without churning identities.
  const companionName = useCompanionName();
  const companionNameRef = useRef(companionName);
  useEffect(() => {
    companionNameRef.current = companionName;
  }, [companionName]);

  // ─── Receipt attachment state (#641 — replaces the retired bill-analysis
  // attach). One synchronous POST /api/documents/upload; the claim is created
  // in the response and OCR enrichment is fire-and-forget backfill, so there
  // is no polling machinery here.
  const [pendingReceipt, setPendingReceipt] = useState<PendingReceipt | null>(null);
  const receiptClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearReceipt = useCallback(() => {
    if (receiptClearTimerRef.current) {
      clearTimeout(receiptClearTimerRef.current);
      receiptClearTimerRef.current = null;
    }
    setPendingReceipt(null);
  }, []);

  // Clean up the submitted-chip auto-clear timer on unmount
  // (SSE lifecycle handled by the core)
  useEffect(() => {
    return () => {
      if (receiptClearTimerRef.current) clearTimeout(receiptClearTimerRef.current);
    };
  }, []);

  // ─── SSE channel extensions (read via ref inside the core) ─────

  const onMessage = useCallback((message: ChatMessageState) => {
    if (message.metadata?.agent) {
      companionDispatch({ type: 'SET_ACTIVE_AGENT', payload: message.metadata.agent as string });
    }
  }, []);

  const onCustomEvent = useCallback((eventName: string, data: unknown, h: ChatCoreHelpers) => {
    switch (eventName) {
      case 'intent':
        companionDispatch({ type: 'SET_ACTIVE_INTENT', payload: (data as SSEIntentPayload).intent });
        break;
      case 'routing':
        companionDispatch({ type: 'SET_ACTIVE_AGENT', payload: (data as SSERoutingPayload).target_agent });
        break;
      case 'safety_check':
        companionDispatch({ type: 'SET_SAFETY_CHECK', payload: data as SSESafetyCheckPayload });
        break;
      case 'escalation':
        companionDispatch({ type: 'SET_ESCALATION', payload: data as SSEEscalationPayload });
        // A critical flag pauses the conversation server-side (#2566); pause
        // it here in the same tick so the composer goes before the next poll.
        if ((data as SSEEscalationPayload).level === 'critical' || (data as { mode?: string }).mode === 'crisis_paused') {
          setMode('crisis_paused');
        }
        break;
      case 'action':
        companionDispatch({ type: 'SET_PENDING_ACTION', payload: data as PendingAction });
        break;
      // ── Handoff lifecycle (default-framed payloads from sseStreams.pushToFamily)
      case 'trfer_message': {
        const payload = data as { message?: { id?: string; content?: string; author_id?: string; timestamp?: string; metadata?: Record<string, unknown> | string } };
        const m = payload.message ?? {};
        // Backend's persisted metadata may arrive as a JSON string
        // (Neo4j stringifies it) or as an already-parsed object
        // (some SSE paths parse it before push). Normalize to an
        // object so callers don't have to handle both shapes.
        let incomingMetadata: Record<string, unknown> = {};
        if (typeof m.metadata === 'string') {
          try { incomingMetadata = JSON.parse(m.metadata); } catch { incomingMetadata = {}; }
        } else if (m.metadata && typeof m.metadata === 'object') {
          incomingMetadata = m.metadata;
        }
        const trferName = (incomingMetadata.trfer_name as string | undefined) ?? tr('chat:handoff.caseManager');
        h.addMessage({
          id: m.id ?? `trfer_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          role: 'trfer',
          content: m.content ?? '',
          timestamp: m.timestamp ?? new Date().toISOString(),
          status: 'complete',
          // Preserve incoming metadata (incl. translation map) plus
          // ensure trfer_name is present so the bubble header renders.
          metadata: { ...incomingMetadata, trfer_name: trferName },
        });
        setMode('human_handoff');
        break;
      }
      case 'trfer_joined': {
        // A TRFer just claimed the open handoff. Backend persisted a system
        // message in Neo4j AND pushed it here so the family sees the join
        // pill in real time, before the TRFer's first message arrives.
        //
        // We mirror the same metadata shape the backend persists so the
        // family-side translation banner can derive the TRFer's language
        // (`trfer_preferred_language`) live without waiting for a refresh
        // to fetch from history. Key is `handoff_request_id` (matches
        // ChatPage's lookup added in B-047) not `handoff_id`.
        const payload = data as {
          trfer_name?: string;
          handoff_id?: string;
          trfer_preferred_language?: string;
          message?: { id?: string; content?: string; timestamp?: string };
        };
        const m = payload.message ?? {};
        const trferName = payload.trfer_name ?? tr('chat:handoff.aCaseManager');
        const metadata: ChatMessageState['metadata'] = {
          event: 'trfer_joined',
          trfer_name: trferName,
          handoff_request_id: payload.handoff_id,
          trfer_preferred_language: payload.trfer_preferred_language ?? 'en',
        };
        const id = m.id ?? `sys_trfer_joined_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
        // The server's own line is stored as it came; only the fallback the
        // client writes is a `{kind, params}` row (R3-B15).
        h.addMessage(
          m.content
            ? { id, role: 'system', content: m.content, timestamp: m.timestamp ?? new Date().toISOString(), status: 'complete', metadata }
            : { ...systemMessage(id, 'trferJoined', { name: trferName }, metadata), timestamp: m.timestamp ?? new Date().toISOString() },
        );
        setMode('human_handoff');
        break;
      }
      case 'message_updated': {
        // Backend pushes this after a per-message retranslate (or any
        // metadata-only refresh) — apply the new metadata in-place so
        // the bubble re-renders (e.g. moves from "Translation unavailable"
        // to a successful translation) without a full reload.
        const payload = data as { message?: { id?: string; metadata?: Record<string, unknown> | string } };
        const m = payload.message ?? {};
        if (!m.id) break;
        let incomingMetadata: Record<string, unknown> = {};
        if (typeof m.metadata === 'string') {
          try { incomingMetadata = JSON.parse(m.metadata); } catch { incomingMetadata = {}; }
        } else if (m.metadata && typeof m.metadata === 'object') {
          incomingMetadata = m.metadata;
        }
        h.updateMessage(m.id, { metadata: incomingMetadata as never });
        break;
      }
      case 'handback': {
        const payload = data as { trfer_name?: string };
        h.addMessage(systemMessage(
          `sys_handback_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          'handback',
          { companion: companionNameRef.current },
          { event: 'handback', trfer_name: payload.trfer_name },
        ));
        setMode('ai_resumed');
        break;
      }
      case 'trfer_idle_released': {
        // The TRFer stepped away (closed the tab or went silent past
        // the heartbeat threshold) and the backend released their claim.
        // Flip the family's view back to AI so the "TRFer is in this
        // conversation" pill drops; another TRFer can claim the open
        // handoff if needed.
        h.addMessage(systemMessage(
          `sys_trfer_idle_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          'trferIdleReleased',
          { companion: companionNameRef.current },
          { event: 'handback' },
        ));
        setMode('ai_resumed');
        break;
      }
      case 'reassigned': {
        const payload = data as { to_trfer?: { first_name?: string; last_name?: string } };
        const toName = payload.to_trfer?.first_name || tr('chat:handoff.anotherCaseManager');
        h.addMessage(systemMessage(
          `sys_reassign_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          'reassigned',
          { name: toName },
          { event: 'reassign', to_trfer_name: toName },
        ));
        break;
      }
      case 'ai_cancelled': {
        // The agent stream was aborted server-side because a handoff opened.
        // Drop any in-flight typing/streaming indicator so the UI stops
        // pretending the companion is still composing.
        h.setStreaming(false);
        h.setTyping(false);
        break;
      }
      case 'handoff_initiated': {
        // An implicit handoff opened server-side (e.g. critical-chat
        // evaluator hit). Flip the mode locally and inject a system pill so
        // the family sees the transition before a TRFer claims and posts.
        h.addMessage(systemMessage(
          `sys_handoff_initiated_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          'handoffInitiated',
          {},
          { event: 'handoff_initiated' },
        ));
        setMode('human_handoff');
        break;
      }
      case 'handoff_sla_breached': {
        // The SLA watcher flipped this handoff to sla_breached. Inject a
        // system message into the conversation history so the breach is
        // recorded inline; ChatPage also reads this event off the message
        // stream to drive the HandoffStatusPill into its 'sla_breached'
        // branch (with a Dismiss button — see B-054).
        //
        // Also flip mode to 'ai_resumed' immediately. The backend's
        // slaWatcher already flips conversation.mode in Neo4j (B-055), but
        // ChatPage only re-reads that via the 30s `getConversations` poll;
        // updating local mode here makes the composer banner ("your
        // messages go to them, not <companion>") drop in the same tick the
        // breach event arrives.
        const payload = data as { handoff_id?: string; severity?: string };
        h.addMessage(systemMessage(
          `sys_sla_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          'slaBreached',
          {},
          { event: 'handoff_sla_breached', handoff_id: payload.handoff_id, severity: payload.severity },
        ));
        setMode('ai_resumed');
        break;
      }
    }
  }, []);

  const onSwitchReset = useCallback(() => {
    clearReceipt();
    companionDispatch({ type: 'RESET' });
    // Reset mode optimistically — ChatPage re-syncs from the conversations
    // summary once the new conversation's metadata is available.
    setMode('ai');
  }, [clearReceipt]);

  // ─── Core engine ───────────────────────────────────────────────

  const core = useChatCore<{ family_id: string; family_name: string } | null>(
    initialConversationId,
    {
      ssePathPrefix: '/api/chat/stream',
      customEvents: CHAT_CUSTOM_EVENTS,
      getHistory: (conversationId) => chatApi.getHistory(conversationId),
      send: (conversationId, content, page_context) =>
        chatApi.sendStreamingMessage(conversationId, content, page_context),
      onMessage,
      onCustomEvent,
      onSwitchReset,
    },
  );
  const { helpers, messages, sendMessage } = core;

  // ─── Receipt attach (after the core: its success line goes in the thread) ─
  /** Returns true on success so the dialog knows whether to close or stay open for retry. */
  const attachReceipt = useCallback(
    async (file: File, category: ExpenseCategory, amount: number): Promise<boolean> => {
      clearReceipt();
      setPendingReceipt({ file, status: 'uploading' });
      try {
        await chatApi.uploadReceipt(file, category, amount);
      } catch (err: unknown) {
        const data = (err as { response?: { data?: { message?: string; code?: string } } })?.response?.data;
        // #2380: a refused file type is named in every language — a file the
        // server refuses can never pass, so "try again" is the wrong advice.
        if (data?.code === 'unsupported_file_type') {
          const reason = tr('money:capture.wrongType');
          setPendingReceipt({ file, status: 'error', error: reason });
          toast({ variant: 'destructive', title: tr('chat:receiptToast.title'), description: reason });
          return false;
        }
        const serverMessage = data?.message;
        const english = isEnglishUi(i18n.language);
        const message =
          serverMessage || (err instanceof Error ? err.message : tr('chat:receiptToast.uploadFailed'));
        setPendingReceipt({ file, status: 'error', error: english ? message : tr('chat:receiptToast.uploadFailed') });
        toast({
          variant: 'destructive',
          title: tr('chat:receiptToast.title'),
          description: english
            ? tr('chat:receiptToast.description', { message })
            : tr('chat:receiptToast.descriptionGeneric'),
        });
        return false;
      }
      // #2379: leave a trace in the thread. A local line, never persisted —
      // the receipt itself lives on the Grants page, which it names. Built
      // in the reader's language now; it does not outlive the session.
      helpers.addMessage({
        id: `sys_receipt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        role: 'system',
        status: 'complete',
        timestamp: new Date().toISOString(),
        content: tr('money:chatReceipt.threadLine', { name: file.name }),
        metadata: { event: 'receipt_sent' },
      });
      setPendingReceipt({ file, status: 'submitted' });
      receiptClearTimerRef.current = setTimeout(() => {
        receiptClearTimerRef.current = null;
        setPendingReceipt(null);
      }, 4000);
      return true;
    },
    [clearReceipt, toast, helpers]
  );


  // ─── Actions ───────────────────────────────────────────────────

  const retryLastMessage = useCallback(() => {
    const lastUserMsg = [...messages]
      .reverse()
      .find((m) => m.role === 'user');
    if (lastUserMsg) {
      // Remove the failed user message and any errored assistant messages after it
      const userIdx = messages.findIndex((m) => m.id === lastUserMsg.id);
      const cleaned = messages.filter(
        (m, i) => i < userIdx || (i > userIdx && m.status !== 'error')
      );
      helpers.setMessages(cleaned);
      helpers.setError(null);
      sendMessage(lastUserMsg.content);
    }
  }, [messages, sendMessage, helpers]);

  const dismissEscalation = useCallback(() => {
    companionDispatch({ type: 'SET_ESCALATION', payload: null });
  }, []);

  const [isConfirming, setIsConfirming] = useState(false);

  const cancelAction = useCallback(() => {
    companionDispatch({ type: 'CLEAR_PENDING_ACTION' });
  }, []);

  const confirmAction = useCallback(async () => {
    const pending = companion.pendingAction;
    if (!pending) return;

    setIsConfirming(true);

    // Each action returns enough to surface an Undo:
    //   { successMessage, undoLabel, onUndo }
    // The toast shows immediately, then offers Undo for 6s. If the user
    // taps Undo we run `onUndo()` (the matching DELETE call). If not, the
    // optimistic commit stands.
    let result: { successMessage: string; undoLabel: string; onUndo: () => Promise<void> } | null = null;

    try {
      if (pending.action === 'create_task') {
        const { id } = await chatApi.createTask(pending.data);
        result = {
          successMessage: tr('chat:actionToasts.taskAdded', { title: pending.data.title }),
          undoLabel: tr('common:actions.undo'),
          onUndo: async () => {
            if (id) await api.tasks.cancel(id, 'Undone from chat');
          },
        };
      }

      if (!result) {
        companionDispatch({ type: 'CLEAR_PENDING_ACTION' });
        return;
      }

      companionDispatch({ type: 'CONFIRM_PENDING_ACTION', payload: { confirmationMessage: result.successMessage } });
      // Clear the inline card sooner than before — the toast carries the
      // Undo affordance now, so the card itself is just confirmation.
      setTimeout(() => companionDispatch({ type: 'CLEAR_PENDING_ACTION' }), 1500);

      let undone = false;
      toast({
        title: tr('chat:actionToasts.done'),
        description: result.successMessage,
        duration: 6000,
        action: (React.createElement(
          ToastAction,
          {
            altText: result.undoLabel,
            className: 'text-stevens-indigo hover:text-stevens-indigo-2 border-stevens-indigo/30 hover:border-stevens-indigo/60',
            onClick: async () => {
              if (undone) return;
              undone = true;
              try {
                await result!.onUndo();
                toast({ title: tr('chat:actionToasts.undone'), description: tr('chat:actionToasts.undoneBody') });
              } catch {
                toast({
                  variant: 'destructive',
                  title: tr('chat:actionToasts.undoFailed'),
                  description: tr('chat:actionToasts.undoFailedBody'),
                });
              }
            },
          },
          result.undoLabel,
        ) as unknown as ToastActionElement),
      });
    } catch (err: unknown) {
      const message = err instanceof Error
        ? serverTextOr(err.message, 'chat:actionToasts.failed')
        : tr('chat:actionToasts.failed');
      helpers.setError(message);
    } finally {
      setIsConfirming(false);
    }
  }, [companion.pendingAction, toast, helpers]);

  // ─── Return ────────────────────────────────────────────────────

  return {
    messages,
    status: core.status,
    isTyping: core.isTyping,
    isStreaming: core.isStreaming,
    error: core.error,
    conversationId: core.conversationId,
    reconnect: core.reconnect,
    activeAgent: companion.activeAgent,
    activeIntent: companion.activeIntent,
    escalation: companion.escalation,
    safetyCheck: companion.safetyCheck,
    pendingAction: companion.pendingAction,
    isConfirming,
    mode,
    setMode,
    sendMessage,
    retryLastMessage,
    clearMessages: core.clearMessages,
    switchConversation: core.switchConversation,
    startNewConversation: core.startNewConversation,
    dismissEscalation,
    dismissError: core.dismissError,
    confirmAction,
    cancelAction,
    pendingReceipt,
    attachReceipt,
    clearReceipt,
    // ── chat/voice parity (#1731, F-4) — engine state passed through ──
    sessionEnded: core.sessionEnded,
    markSessionEnded: core.markSessionEnded,
    markSessionResumed: core.markSessionResumed,
    isVoiceThread: core.isVoiceThread,
    versioningEnabled: core.versioningEnabled,
    reloadMessages: core.reloadMessages,
    retryMessage: core.retryMessage,
    chips: core.chips,
    recall: core.recall,
    failure: core.failure,
    reanchorKey: core.reanchorKey,
  };
}
